import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Link from '@g4/next-furniture/Link';
import { TrackClick } from '@g4/trackster';
import { hook } from '@g4/automation-hooks';
import { useUserContext } from '@g4/next-furniture/UserContextProvider';
import { useAuthentication } from '../../../../graphql';

import getIcon from './navItem/getIcon';
import { LOGOUT_PATH } from '../../../../utils/authUtils';
import { useMedia, VerticalSpacing, Box } from '../../../Boneyard';
import Markdown from '../../../Markdown';

import ARROW_ICON from '../../../../images/ic-arrow-blue.svg';

const propTypes = {
  item: PropTypes.object.isRequired,
  defaultBackgroundType: PropTypes.oneOf(['light', 'dark']),
  isMegaMenuLink: PropTypes.bool,
  showArrow: PropTypes.bool,
  isCustomMenu: PropTypes.bool,
  isMultiLineItem: PropTypes.bool,
  isOpen: PropTypes.bool,
  automationHook: PropTypes.string,
  onclick: PropTypes.func,
};

const defaultProps = {
  defaultBackgroundType: 'light',
  isMegaMenuLink: false,
  showArrow: false,
  isMultiLineItem: false,
  isOpen: false,
};

// prettier-ignore
const StyledSpan = styled.span`
  position: relative;
  display: flex;
  flex-direction: ${props => props.displayAsColumn ? 'column' : 'row'};
  flex: 1 0 auto;
  align-items:  ${props => props.displayAsColumn ? 'flex-start' : 'center'};
  width: 100%;
  height: 100%;
  max-width: 100%;
  padding: ${props => props.theme.spacing(2)} ${props => props.theme.spacing(props.isMegaMenuLink ? 0 : 4)};
  font-weight: 500;
  font-style: normal;
  font-size: ${props => props.style?.fontSize ? props.style.fontSize : 'normal'};
  line-height: normal;
  letter-spacing: normal;
  text-decoration: none;
  cursor: pointer;
  color: ${props => props.isMegaMenuLink ? props.theme.colors.siteHeader.megaMenu.linkColor : props.theme.colors.siteHeader.light.linkColor};
  color: ${props => props.style?.fontColor ? props.style.fontColor : ''};

  ${props => props.defaultBackgroundType === 'dark' && `color: ${props.theme.colors.siteHeader.dark.linkColor}`};

  ${props => props.theme.query.xs} {
    padding-left: 0;
    color: ${props => props.theme.colors.siteHeader.light.linkColor}
  }

  img {
    height: 20px;
    flex-shrink: 0;
    margin-right: ${props => props.theme.spacing(1)};

    transition: opacity 0.2s ease-in-out;
  }

  ${props => (props.showArrow || props.isInteractive) && `
    border-bottom: 5px solid transparent;

    border-bottom-color: ${props.isActive
      ? props.theme.colors.siteHeader.linkActive
      : 'transparent'};

    transition: all 0.2s ease-in-out;

    &:hover,
    &:focus,
    &:focus-within {
      outline: 0;
      border-bottom-color: ${props.theme.colors.siteHeader.linkActive};
    }
  `}
  ${props => (props.showArrow || props.isInteractive) && props.theme.query.xs} {
    border-bottom: 0;
  }

  ${props => props.showArrow && `
    padding-right: ${props.theme.spacing(5)};
    padding-bottom: ${props.theme.spacing(2)};
    &::after {
      content: '';
      color: ${props.isCustomMenu ? props.theme.colors.siteHeader.linkActive : 'inherit'};
      margin-left: ${props.theme.spacing(1)};
      margin-bottom: ${props.isMultiLineItem ? props.theme.spacing(5) : 0};

      border-left: 7px solid transparent;
      border-right: 7px solid transparent;
      border-top: 7px solid;

      transition: transform 0.3s ease-in-out;

      ${props.isOpen ? `
        transform: scaleY(-1);
      ` : ''}
    }
  `}
  ${props => props.showArrow && props.theme.query.xs} {
    &::after {
      border: 0;
      background-image: url(${ARROW_ICON});
      width: 17px;
      height: 17px;
    }
  }
`;

const PhoneNumber = styled.div`
  color: ${props => props.theme.colors.siteHeader.light.linkColor};
`;

const NavContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const isLogoutLink = link => link && link.path === LOGOUT_PATH;

const NavItem = ({
  item,
  isCustomMenu,
  defaultBackgroundType,
  isMegaMenuLink,
  showArrow,
  isMultiLineItem,
  isOpen,
  onclick,
  automationHook,
  ...rest
}) => {
  const mq = useMedia();
  const isDesktop = mq.gte('md');
  const { title, style, description, icon, content, link } = item;
  const { isTravelAgent } = useUserContext();

  const { logout, iataNumber } = useAuthentication();

  const handleKeyPress = event => {
    if (event.key === 'Enter') {
      onclick(event);
    }
  };

  if (link && link.path !== '/<nolink>') {
    const href = link.path || link.url;
    const linkTarget = `_${link.target.toLowerCase()}`;
    const logoutCallback = isLogoutLink(link) && {
      onClick: e => {
        e.preventDefault();
        logout && logout();
      },
    };

    return (
      <Link href={href} target={linkTarget}>
        {({ anchorProps }) => (
          <TrackClick action="Top Nav" label={title}>
            <StyledSpan
              as="a"
              defaultBackgroundType={defaultBackgroundType}
              isMegaMenuLink={isMegaMenuLink}
              displayAsColumn={content}
              isInteractive
              {...anchorProps}
              {...hook(automationHook)}
              {...logoutCallback}
            >
              {icon && isDesktop && (
                <img src={getIcon(icon, defaultBackgroundType)} alt="" />
              )}
              {title}
              {content && <PhoneNumber>{content}</PhoneNumber>}
            </StyledSpan>
          </TrackClick>
        )}
      </Link>
    );
  }

  return (
    <>
      <StyledSpan
        defaultBackgroundType={defaultBackgroundType}
        isMegaMenuLink={isMegaMenuLink}
        displayAsColumn={content}
        style={style}
        showArrow={showArrow}
        isMultiLineItem={isMultiLineItem}
        isCustomMenu={isCustomMenu}
        onClick={onclick}
        isInteractive
        as={showArrow ? 'button' : undefined}
        isOpen={isOpen}
        {...hook(automationHook)}
        {...rest}
        tabIndex={0}
        onKeyPress={handleKeyPress}
      >
        <NavContent>
          {!isTravelAgent && (
            <>
              <Box d={'flex'} justifyContent="center" alignItems="center">
                {icon && (
                  <img src={getIcon(icon, defaultBackgroundType)} alt="" />
                )}
                {title}
                {content && <PhoneNumber>{content}</PhoneNumber>}
              </Box>

              {description && (
                <>
                  <VerticalSpacing size={1} />
                  <Box d={'flex'} justifyContent="center" alignItems="center">
                    <img
                      src={getIcon(description.icon, defaultBackgroundType)}
                      alt=""
                    />
                    <Markdown source={description.details} />
                  </Box>
                </>
              )}
            </>
          )}

          {isTravelAgent && (
            <>
              <Box d={'flex'} justifyContent="center" alignItems="center">
                {icon && (
                  <img src={getIcon(icon, defaultBackgroundType)} alt="" />
                )}
                {title}
              </Box>
              <VerticalSpacing size={1} />
              <Box d={'flex'} justifyContent="center" alignItems="center">
                <Markdown
                  source={description.details}
                  data={{ iataNumber: iataNumber }}
                />
              </Box>
            </>
          )}
        </NavContent>
      </StyledSpan>
    </>
  );
};

NavItem.propTypes = propTypes;

NavItem.defaultProps = defaultProps;

export default NavItem;
